import { useState } from "react";
import axios from "axios";
import apiurl from "../Api";
import Swal from "sweetalert2";

function ChangePassword() {
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.currentPassword && (!form.newPassword || !form.confirmPassword)) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please enter new password and confirm password.",
        icon: "warning",
      });
      return;
    }
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all fields.",
        icon: "warning",
      });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      Swal.fire({
        title: "Password Mismatch",
        text: "New password and confirm password must match.",
        icon: "error",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Update",
      text: "Are you sure you want to update your password?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const response = await axios.post(apiurl + "change_password.php", new URLSearchParams({
        current_password: form.currentPassword,
        new_password: form.newPassword,
      }));

      if (response.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data?.message || "Password updated successfully.",
          icon: "success",
        });
        setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        Swal.fire({
          title: "Error",
          text: response.data?.message || "Unable to update password.",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Unable to update password.",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content app-content">
      <style>{`
        .change-password-page .panel-card {
          border-radius: 16px;
          border: 1px solid #e6edf5;
          box-shadow: 0 18px 36px rgba(15, 42, 80, 0.16);
        }
        .change-password-page .panel-hero {
          background:
            radial-gradient(120% 140% at 100% 0%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 55%),
            linear-gradient(135deg, rgb(16, 40, 92) 0%, rgb(34, 74, 156) 48%, rgb(56, 118, 210) 100%);
          border-radius: 16px;
          color: #fff;
          padding: 20px;
          box-shadow: 0 18px 40px rgba(12, 46, 110, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.08);
          position: relative;
          overflow: hidden;
        }
        .change-password-page .panel-hero .hero-art {
          position: absolute;
          right: -8px;
          top: -8px;
          opacity: 0.3;
        }
        .change-password-page .form-label {
          color: #5a6a85;
          font-weight: 600;
          font-size: 13px;
        }
        .change-password-page .form-control {
          border-radius: 12px;
          border: 1px solid #d7e2f2;
          background: #fbfdff;
        }
        .change-password-page .form-control:focus {
          border-color: #6aa7ff;
          box-shadow: 0 0 0 0.2rem rgba(88, 140, 255, 0.15);
        }
        .change-password-page .mb-10 {
          margin-bottom: 6rem !important;
        }
        .change-password-page .input-group-text,
        .change-password-page .toggle-btn {
          border-radius: 12px;
        }
      `}</style>
      <div className="container-fluid change-password-page">
        <div className="row g-4">
          <div className="col-12">
            <div className="panel-hero mt-3">
              <div className="fs-4 fw-semibold">Change Password</div>
              <div className="text-white-50">Keep your account secure by updating your password regularly.</div>
              <svg
                className="hero-art"
                width="220"
                height="140"
                viewBox="0 0 220 140"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M16 90C58 40 112 14 202 18"
                  stroke="#022045"
                  strokeWidth="18"
                  strokeLinecap="round"
                  opacity="0.35"
                />
                <path
                  d="M22 118C70 78 132 60 196 66"
                  stroke="#022045"
                  strokeWidth="10"
                  strokeLinecap="round"
                  opacity="0.35"
                />
              </svg>
            </div>
          </div>

          <div className="col-lg-6 mx-auto">
            <div className="panel-card p-4 mt-3 mb-10">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <div className="input-group">
                    <input
                      type={showCurrent ? "text" : "password"}
                      className="form-control"
                      value={form.currentPassword}
                      onChange={handleChange("currentPassword")}
                      placeholder="Enter current password"
                    />
                    <button
                      className="btn btn-light toggle-btn"
                      type="button"
                      onClick={() => setShowCurrent((prev) => !prev)}
                      aria-label={showCurrent ? "Hide current password" : "Show current password"}
                    >
                      <i className={showCurrent ? "ri-eye-line" : "ri-eye-off-line"} />
                    </button>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <div className="input-group">
                    <input
                      type={showNew ? "text" : "password"}
                      className="form-control"
                      value={form.newPassword}
                      onChange={handleChange("newPassword")}
                      placeholder="Enter new password"
                    />
                    <button
                      className="btn btn-light toggle-btn"
                      type="button"
                      onClick={() => setShowNew((prev) => !prev)}
                      aria-label={showNew ? "Hide new password" : "Show new password"}
                    >
                      <i className={showNew ? "ri-eye-line" : "ri-eye-off-line"} />
                    </button>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label">Confirm New Password</label>
                  <div className="input-group">
                    <input
                      type={showConfirm ? "text" : "password"}
                      className="form-control"
                      value={form.confirmPassword}
                      onChange={handleChange("confirmPassword")}
                      placeholder="Confirm new password"
                    />
                    <button
                      className="btn btn-light toggle-btn"
                      type="button"
                      onClick={() => setShowConfirm((prev) => !prev)}
                      aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                    >
                      <i className={showConfirm ? "ri-eye-line" : "ri-eye-off-line"} />
                    </button>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={saving}
                  >
                    {saving ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
